import { useMemo, useState } from 'react';
import { Flex } from '@mantine/core';
import CustomSpinner from '@/components/Spinner.tsx';
import TextSearchBox from '@/components/TextSearchBox.tsx';
import { useDebouncedValue } from '@mantine/hooks';
import { flattenInfiniteQueryData } from '@/utils/misc.ts';
import { SHORT_DEBOUNCE_TIME } from '@/utils/constants.ts';
import AutomationPageManualRunButton from '@/pages/AutomationPage/components/ManualRunModal/components/AutomationPageManualRunButton.tsx';
import RunsCards from '@/pages/AutomationPage/components/RunsCards.tsx';
import useRetrieveAutomationRunsWithDatasets from '@/hooks/api/runs/useRetrieveAutomationRunsWithDatasets.ts';
import AutomationPageWrapper from '@/layouts/AutomationPageWrapper/AutomationPageWrapper.tsx';
import AutomationPageHeader from '@/pages/AutomationPage/components/AutomationPageHeader.tsx';
import { useAutomation } from '@/context/AutomationContext/useAutomationContext.ts';
import FilterSelect from '@/pages/Run/components/RunHistorySidebar/FilterSelect.tsx';
import useRunFilters from '@/hooks/useRunFilters.ts';
import AutomationSuiteLinks from '@/pages/AutomationSuites/AutomationSuiteLinks.tsx';
import ScheduledTriggers from '@/pages/AutomationPage/components/ScheduledTriggers.tsx';

export function AutomationPage() {
  const automation = useAutomation();
  const {
    filterStatuses,
    filterRunOrigins,
    filterRunModes,
    filterProvenanceStates,
    toggleRunMode,
    toggleStatus,
    toggleRunOrigin,
    toggleProvenanceState,
    onlyRunOrigin,
    onlyStatus,
    onlyRunMode,
    onlyProvenanceState,
    resetFilters,
    originOptions,
    modeOptions,
    provenanceOptions,
    statusOptions,
    resetStatuses,
  } = useRunFilters();

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery] = useDebouncedValue(searchQuery, SHORT_DEBOUNCE_TIME);

  const {
    data: runs,
    isPending: isLoadingRuns,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useRetrieveAutomationRunsWithDatasets({
    statuses: filterStatuses,
    search: debouncedSearchQuery,
    runModes: filterRunModes,
    runOrigins: filterRunOrigins,
    provenanceStates: filterProvenanceStates,
  });

  const allRuns = useMemo(() => flattenInfiniteQueryData(runs), [runs]);

  return (
    <Flex w="100%" h="100%">
      <AutomationPageWrapper automation={automation} isOuterWrapper={false}>
        <Flex
          direction="column"
          w="100%"
          h="100%"
          px="50px"
          pt="lg"
          style={{ overflowY: 'auto', overflowX: 'hidden' }}
        >
          <AutomationPageHeader
            automation={automation}
            filterStatuses={filterStatuses}
            toggleStatus={toggleStatus}
            onlyStatus={onlyStatus}
            resetStatuses={resetStatuses}
          />
          <AutomationSuiteLinks key={automation.id} automationId={automation.id} />
          <ScheduledTriggers automationId={automation.id} />

          <Flex mt={'60px'} mb="lg" align={'center'} justify={'space-between'}>
            <Flex gap={'md'} align={'center'}>
              <TextSearchBox
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                placeholder={'Search all runs'}
              />
              <FilterSelect
                statuses={filterStatuses}
                toggleStatus={toggleStatus}
                onlyStatus={onlyStatus}
                statusOptions={statusOptions}
                runModes={filterRunModes}
                toggleRunMode={toggleRunMode}
                onlyRunMode={onlyRunMode}
                modeOptions={modeOptions}
                runOrigins={filterRunOrigins}
                toggleRunOrigin={toggleRunOrigin}
                onlyRunOrigin={onlyRunOrigin}
                originOptions={originOptions}
                provenanceStates={filterProvenanceStates}
                toggleProvenanceState={toggleProvenanceState}
                onlyProvenanceState={onlyProvenanceState}
                provenanceOptions={provenanceOptions}
                resetFilters={resetFilters}
              />
            </Flex>
            <AutomationPageManualRunButton automation={automation} />
          </Flex>
          {isLoadingRuns ? (
            <CustomSpinner />
          ) : (
            <RunsCards
              runs={allRuns}
              hasNextPage={hasNextPage}
              fetchNextPage={fetchNextPage}
              isFetchingNextPage={isFetchingNextPage}
            />
          )}
        </Flex>
      </AutomationPageWrapper>
    </Flex>
  );
}

export default AutomationPage;
